import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Avatar,
  Chip,
  Divider,
  Spinner,
  Input,
  Select,
  SelectItem,
  Card,
  CardBody,
  CardHeader,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { ErrorState } from './error-state';
import { useAuth } from '../stores/auth-store';
import { useAuthStore } from '../stores/auth-store';
import { api } from '../utils/api';
import { toast } from '../stores/ui-store';
import { mediaService } from '../services/media.service';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProfileModal: FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { fetchProfile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('fa');
  const [gender, setGender] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchProfileData();
    }
  }, [isOpen]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/account/profile/');
      
      const data = {
        id: response.id,
        name: response.fullname || response.email?.split('@')[0] || 'User',
        username: response.email?.split('@')[0] || 'user',
        email: response.email || '',
        avatar: response.avatar_url || null,
        role: response.role || 'user',
        phone: response.phone_number || null,
        status: response.is_active ?? true,
        permissionGroups: response.groups || [],
        permissions: response.all_permissions || [],
        last_login: response.last_login || null,
        created_at: response.date_joined || new Date().toISOString(),
      };
      
      setProfileData(data);
      setFullname(response.fullname || '');
      setEmail(response.email || '');
      setPhoneNumber(response.phone_number || '');
      setCity(response.city || '');
      setCountry(response.country || '');
      setLanguage(response.language || 'fa');
      setGender(response.gender || '');
      setAvatarPreview(response.avatar_url || null);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error(t('errorLoadingProfile'));
      setLoading(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!fullname.trim()) {
      toast.error(t('fullnameRequired'));
      return;
    }

    try {
      setSubmitting(true);
      
      let uploadedAvatarPath = null;
      if (avatarFile) {
        try {
          const uploadResult = await mediaService.uploadTemp(avatarFile);
          uploadedAvatarPath = uploadResult.file;
        } catch (uploadError) {
          console.error('Error uploading avatar:', uploadError);
          toast.error(t('errorUploadingAvatar'));
          setSubmitting(false);
          return;
        }
      }
      
      const payload: any = { fullname };
      if (phoneNumber) payload.phone_number = phoneNumber;
      if (city) payload.city = city;
      if (country) payload.country = country;
      if (language) payload.language = language;
      if (gender) payload.gender = gender;
      if (uploadedAvatarPath) payload.avatar = uploadedAvatarPath;

      // PATCH request updates the profile and returns the complete updated profile
      const response = await api.patch('/dashboard/account/profile/', payload);
      
      // Update auth store manually (without calling fetchProfile to avoid duplicate GET)
      useAuthStore.setState({
        user: {
          id: response.id,
          email: response.email,
          fullname: response.fullname,
          avatar: response.avatar_url || response.avatar,
          avatar_url: response.avatar_url,
          phone_number: response.phone_number,
          gender: response.gender,
          city: response.city,
          country: response.country,
          language: response.language,
          role: response.role,
          is_superuser: response.is_superuser,
          is_staff: response.is_staff,
          is_active: response.is_active,
          date_joined: response.date_joined,
        },
        permissions: response.all_permissions || [],
        groups: response.groups || [],
      });
      
      // Update local modal state from the same response
      const data = {
        id: response.id,
        name: response.fullname || response.email?.split('@')[0] || 'User',
        username: response.email?.split('@')[0] || 'user',
        email: response.email || '',
        avatar: response.avatar_url || null,
        role: response.role || 'user',
        phone: response.phone_number || null,
        status: response.is_active ?? true,
        permissionGroups: response.groups || [],
        permissions: response.all_permissions || [],
        last_login: response.last_login || null,
        created_at: response.date_joined || new Date().toISOString(),
      };
      setProfileData(data);
      setFullname(response.fullname || '');
      setEmail(response.email || '');
      setPhoneNumber(response.phone_number || '');
      setCity(response.city || '');
      setCountry(response.country || '');
      setLanguage(response.language || 'fa');
      setGender(response.gender || '');
      setAvatarPreview(response.avatar_url || null);
      
      toast.success(t('profileUpdatedSuccessfully'));
      setIsEditMode(false);
      setAvatarFile(null);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.email) {
        toast.error(t('emailAlreadyExists'));
      } else {
        toast.error(t('errorUpdatingProfile'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setAvatarFile(null);
    if (profileData) {
      setFullname(profileData.name);
      setEmail(profileData.email);
      setPhoneNumber(profileData.phone || '');
      setAvatarPreview(profileData.avatar);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('never');
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return t('invalidDate');
      return new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      return t('invalidDate');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'editor': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Icon icon={isEditMode ? "lucide:user-cog" : "lucide:user-circle"} width={24} className="text-primary" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-lg font-semibold">{isEditMode ? t('editProfile') : t('userDetails')}</span>
              <span className="text-sm font-normal text-default-500">
                {isEditMode ? t('updateYourPersonalInformation') : t('viewAndManageYourProfile')}
              </span>
            </div>
          </div>
          {!isEditMode && !loading && (
            <Button color="primary" size="sm" startContent={<Icon icon="lucide:edit" width={16} />} onPress={() => setIsEditMode(true)}>
              {t('editProfile')}
            </Button>
          )}
        </ModalHeader>
        <Divider />
        <ModalBody className="px-6 py-4">
          {loading ? (
            <div className="flex justify-center items-center h-96"><Spinner size="lg" color="primary" /></div>
          ) : !profileData ? (
            <ErrorState 
              message={t('errorLoadingProfile')}
              variant="inline"
              errorType="server"
            />
          ) : isEditMode ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <Card>
                    <CardHeader><h3 className="text-sm font-semibold flex items-center gap-2">
                      <Icon icon="lucide:image" width={18} className="text-primary" />{t('profilePicture')}</h3></CardHeader>
                    <CardBody className="items-center text-center space-y-4">
                      <Avatar src={avatarPreview || undefined} fallback={<Icon icon="lucide:user" width={48} />} className="w-32 h-32" isBordered color="primary" />
                      <div className="w-full">
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" id="avatar-upload" />
                        <label htmlFor="avatar-upload">
                          <Button as="span" variant="flat" color="primary" size="sm" startContent={<Icon icon="lucide:upload" width={16} />} className="cursor-pointer">
                            {t('uploadPhoto')}
                          </Button>
                        </label>
                      </div>
                      <p className="text-xs text-default-400">{t('allowedFormats')}: JPG, PNG, GIF</p>
                    </CardBody>
                  </Card>
                </div>
                <div className="lg:col-span-2 space-y-4">
                  <Card>
                    <CardHeader><h3 className="text-sm font-semibold flex items-center gap-2">
                      <Icon icon="lucide:user" width={18} className="text-primary" />{t('personalInformation')}</h3></CardHeader>
                    <CardBody className="space-y-4">
                      <Input label={t('fullName')} placeholder={t('enterFullName')} value={fullname} onChange={(e) => setFullname(e.target.value)} isRequired labelPlacement="outside" size="sm" startContent={<Icon icon="lucide:user" width={16} className="text-default-400" />} />
                      <Input label={t('email')} type="email" placeholder={t('enterEmail')} value={email} onChange={(e) => setEmail(e.target.value)} isRequired isReadOnly labelPlacement="outside" size="sm" startContent={<Icon icon="lucide:mail" width={16} className="text-default-400" />} />
                      <Input label={t('phoneNumber')} type="tel" placeholder={t('enterPhoneNumber')} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} labelPlacement="outside" size="sm" startContent={<Icon icon="lucide:phone" width={16} className="text-default-400" />} dir="ltr" />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select label={t('gender')} placeholder={t('selectGender')} selectedKeys={gender ? [gender] : []} onChange={(e) => setGender(e.target.value)} labelPlacement="outside" size="sm">
                          <SelectItem key="male">{t('maleGender')}</SelectItem>
                          <SelectItem key="female">{t('femaleGender')}</SelectItem>
                        </Select>
                        <Select label={t('language')} placeholder={t('selectLanguage')} selectedKeys={language ? [language] : []} onChange={(e) => setLanguage(e.target.value)} labelPlacement="outside" size="sm">
                          <SelectItem key="fa">{t('persianLang')}</SelectItem>
                          <SelectItem key="en">{t('englishLang')}</SelectItem>
                          <SelectItem key="ar">{t('arabicLang')}</SelectItem>
                        </Select>
                      </div>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader><h3 className="text-sm font-semibold flex items-center gap-2">
                      <Icon icon="lucide:map-pin" width={18} className="text-primary" />{t('locationInformation')}</h3></CardHeader>
                    <CardBody>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label={t('city')} placeholder={t('enterCity')} value={city} onChange={(e) => setCity(e.target.value)} labelPlacement="outside" size="sm" />
                        <Input label={t('country')} placeholder={t('enterCountry')} value={country} onChange={(e) => setCountry(e.target.value)} labelPlacement="outside" size="sm" />
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-4">
                <Card>
                  <CardBody className="items-center text-center space-y-4 p-6">
                    <Avatar src={profileData.avatar || undefined} fallback={<Icon icon="lucide:user" width={48} />} className="w-32 h-32" isBordered color="primary" />
                    <div className="space-y-2">
                      <h2 className="text-xl font-semibold">{profileData.name}</h2>
                      <p className="text-sm text-default-500">@{profileData.username}</p>
                      <Chip color={getRoleColor(profileData.role)} variant="flat" size="sm" startContent={<Icon icon="lucide:shield" width={14} />}>
                        {t(profileData.role)}
                      </Chip>
                    </div>
                    {profileData.status && <Chip color="success" variant="dot" size="sm">{t('active')}</Chip>}
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader className="pb-0"><h3 className="text-sm font-semibold flex items-center gap-2">
                    <Icon icon="lucide:activity" width={16} className="text-primary" />{t('statistics')}</h3></CardHeader>
                  <CardBody className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-default-500">{t('permissionGroups')}</span>
                      <span className="font-semibold">{profileData.permissionGroups?.length || 0}</span>
                    </div>
                    <Divider />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-default-500">{t('permissions')}</span>
                      <span className="font-semibold">
                        {profileData.permissions?.length === 1 && profileData.permissions[0] === '*' ? t('allPermissions') : profileData.permissions?.length || 0}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </div>
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader className="pb-0"><h3 className="text-sm font-semibold flex items-center gap-2">
                    <Icon icon="lucide:mail" width={18} className="text-primary" />{t('contactInformation')}</h3></CardHeader>
                  <CardBody className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-default-400">{t('email')}</p>
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Icon icon="lucide:mail" width={14} className="text-default-400" />{profileData.email}
                        </p>
                      </div>
                      {profileData.phone && (
                        <div className="space-y-1">
                          <p className="text-xs text-default-400">{t('phone')}</p>
                          <p className="text-sm font-medium flex items-center gap-2" dir="ltr">
                            <Icon icon="lucide:phone" width={14} className="text-default-400" />{profileData.phone}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader className="pb-0"><h3 className="text-sm font-semibold flex items-center gap-2">
                    <Icon icon="lucide:info" width={18} className="text-primary" />{t('accountInformation')}</h3></CardHeader>
                  <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-default-400">{t('username')}</p>
                        <p className="text-sm font-medium">@{profileData.username}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-default-400">{t('userId')}</p>
                        <p className="text-sm font-medium">#{profileData.id}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-default-400">{t('accountCreated')}</p>
                        <p className="text-sm font-medium">{formatDate(profileData.created_at)}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-default-400">{t('lastLogin')}</p>
                        <p className="text-sm font-medium">{profileData.last_login ? formatDate(profileData.last_login) : t('never')}</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                {profileData.permissionGroups && profileData.permissionGroups.length > 0 && (
                  <Card>
                    <CardHeader className="pb-0"><h3 className="text-sm font-semibold flex items-center gap-2">
                      <Icon icon="lucide:users" width={18} className="text-primary" />{t('permissionGroups')}</h3></CardHeader>
                    <CardBody>
                      <div className="flex flex-wrap gap-2">
                        {profileData.permissionGroups.map((group: any) => (
                          <Chip key={group.id} variant="flat" color="secondary" size="sm" startContent={<Icon icon="lucide:shield" width={12} />}>
                            {group.name}
                          </Chip>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <Divider />
        <ModalFooter className="px-6 py-4">
          {isEditMode ? (
            <div className="flex justify-end gap-3 w-full">
              <Button variant="flat" onPress={handleCancel} isDisabled={submitting}>{t('cancel')}</Button>
              <Button color="primary" onPress={handleSubmit} isLoading={submitting} startContent={!submitting && <Icon icon="lucide:save" width={18} />}>
                {t('saveChanges')}
              </Button>
            </div>
          ) : (
            <Button color="default" variant="light" onPress={onClose}>{t('close')}</Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
